<script setup lang="ts">
import type { Product } from '~/composables/useApi'

const props = defineProps<{
  items: Array<Product>
  categories?: Array<string>
  showGiftKey?: boolean
  purchaseDisabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'click' | 'submit', priceId: string): void
  (e: 'clickGiftKey' | 'submitGiftKey'): void
}>()

const categoriesToShow = computed(() => props.categories ?? ['encodia:bundle', 'encodia', 'plan', 'support'])

// filter products by categories and sorted by category order
const filteredProducts = computed(() =>
  props.items.filter(item =>
    categoriesToShow.value.some(filter => item.category.startsWith(filter)),
  ).sort((a, b) => {
    const aIndex = categoriesToShow.value.indexOf(a.category)
    const bIndex = categoriesToShow.value.indexOf(b.category)
    if (aIndex === bIndex) {
      return a.sortOrder - b.sortOrder
    }
    return aIndex - bIndex
  }),
)

const onClickProduct = (priceId: string): void => {
  emit('click', priceId)
}

const onSubmitProduct = (priceId: string): void => {
  if (props.purchaseDisabled) return
  emit('submit', priceId)
}
</script>

<template>
  <div class="product-scroll-area">
    <v-container
      class="px-0 pt-0"
      fluid
    >
      <transition-group
        name="product"
        tag="div"
        class="product-wrapper"
      >
        <EmporiumProductCard
          v-for="item in filteredProducts"
          :key="item.productId"
          class="product-item-wrapper"
          :product-id="item.productId"
          :title="item.title"
          :price-id="item.priceId"
          :amount="item.amount"
          :sale-title="item.saleTitle"
          :billing-cycle="item.billingCycle"
          :is-unlocked="item.isUnlocked"
          :is-processing="item.isProcessing"
          :is-purchased="item.isPurchased"
          :is-new="item.isNew"
          :purchase-disabled="purchaseDisabled"
          @click="onClickProduct(item.priceId)"
          @submit="onSubmitProduct(item.priceId)"
        />
        <EmporiumGiftKeyCard
          v-if="showGiftKey"
          key="gift-key"
          class="product-item-wrapper"
          @click="emit('clickGiftKey')"
          @submit="emit('submitGiftKey')"
        />
      </transition-group>
    </v-container>
  </div>
</template>

<style scoped>
.product-scroll-area {
  padding-top: max(4px, calc(0.5 * var(--u)));
  flex: 1 1 auto;
  min-height: 0;
}

.product-wrapper {
  --grid-gap: max(6px, calc(0.75 * var(--u)));

  width: 100%;
  padding: 0 var(--grid-gap);

  display: flex !important;
  flex-wrap: wrap !important;
  gap: var(--grid-gap);

  justify-content: flex-start !important;
  align-items: flex-start;
}

.product-item-wrapper {
  width: calc((100% - 4 * var(--grid-gap)) / 5);
}
</style>
